/**
 * RichTextEditor — TipTap-based WYSIWYG for Doserne Daily Plans.
 *
 * Toolbar features:
 *  - Headings (H1, H2, paragraph)
 *  - Bold / Italic / Underline / Strike
 *  - Bullet + numbered lists
 *  - Text color + highlight (yellow)
 *  - Link (add/remove)
 *  - Alignment (left / center / right)
 *  - Emoji picker (full, with search)
 *
 * Emits HTML (safe on our side because we sanitize with DOMPurify on render).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, Color } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import EmojiPicker, { EmojiStyle, Theme } from "emoji-picker-react";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Heading1, Heading2, Link2, Link2Off,
  AlignLeft, AlignCenter, AlignRight, Smile, Highlighter, Palette, Camera, ClipboardList, HardHat,
} from "lucide-react";
import { getNearestProjectId } from "./planEditorHelpers";

const TEXT_COLORS = [
  { name: "Default", value: "#0f172a" },
  { name: "Red",     value: "#dc2626" },
  { name: "Orange",  value: "#ea580c" },
  { name: "Amber",   value: "#d97706" },
  { name: "Green",   value: "#059669" },
  { name: "Blue",    value: "#2563eb" },
  { name: "Purple",  value: "#7c3aed" },
  { name: "Slate",   value: "#64748b" },
];

function ToolbarButton({ active, disabled, onClick, title, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`inline-flex items-center justify-center h-8 min-w-8 px-1.5 rounded transition text-slate-700 hover:bg-slate-200 active:bg-slate-300 disabled:opacity-30 disabled:hover:bg-transparent ${active ? "bg-slate-900 text-white hover:bg-slate-800" : ""}`}
    >
      {children}
    </button>
  );
}

function ToolbarSep() {
  return <div className="w-px h-5 bg-slate-300 mx-0.5" />;
}

export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "",
  minHeight = 160,
  testid = "rich-editor",
  onAddPhoto, // Optional: () => Promise<{url, label}> — if provided, adds a "Photo" toolbar button.
  onAddMaterialList, // Optional: () => Promise<{id, title}|null> — if provided, adds a "Material List" button.
  onOpenMaterialList, // Optional: (listId) => void — click handler for #list:LIST_ID anchors.
  onAddProject,  // Optional: () => Promise<{id, number, name, address}|null>
  onOpenProject, // Optional: (projectId) => void — click handler for #project:ID anchors
}) {
  const [showEmoji, setShowEmoji] = useState(false);
  const [showColors, setShowColors] = useState(false);
  const emojiBtnRef = useRef(null);
  const colorBtnRef = useRef(null);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2] },
        link: { openOnClick: false, autolink: true, HTMLAttributes: { class: "underline text-emerald-700" } },
      }),
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
      Highlight.configure({ multicolor: false, HTMLAttributes: { class: "bg-yellow-200 rounded px-0.5" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Tiptap emits <p></p> for an empty doc — normalize to "".
      const cleaned = html === "<p></p>" ? "" : html;
      onChange?.(cleaned);
    },
  });

  // Sync external value → editor when it changes (edit mode).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (next && next !== current) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, editor]);

  // Intercept clicks on <a href="#list:ID"> and <a href="#project:ID"> anchors.
  // The editor is contentEditable so clicks normally don't navigate; we still
  // capture at the editor DOM level to open the modal from within the editor.
  useEffect(() => {
    if (!editor) return;
    if (!onOpenMaterialList && !onOpenProject) return;
    const dom = editor.view.dom;
    const handler = (e) => {
      const a = e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (href.startsWith("#list:") && onOpenMaterialList) {
        e.preventDefault(); e.stopPropagation();
        onOpenMaterialList(href.slice(6), a);
        return;
      }
      if (href.startsWith("#project:") && onOpenProject) {
        e.preventDefault(); e.stopPropagation();
        onOpenProject(href.slice(9), a);
      }
    };
    dom.addEventListener("click", handler, true);
    return () => dom.removeEventListener("click", handler, true);
  }, [editor, onOpenMaterialList, onOpenProject]);

  // Close popovers on outside click.
  useEffect(() => {
    if (!showEmoji && !showColors) return;
    const onDoc = (e) => {
      if (showEmoji && emojiBtnRef.current && !emojiBtnRef.current.contains(e.target)) setShowEmoji(false);
      if (showColors && colorBtnRef.current && !colorBtnRef.current.contains(e.target)) setShowColors(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showEmoji, showColors]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL:", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return (
      <div
        className="border border-slate-300 rounded-lg bg-white"
        style={{ minHeight: minHeight + 40 }}
      />
    );
  }

  return (
    <div className="border border-slate-300 rounded-lg bg-white overflow-visible focus-within:ring-2 focus-within:ring-emerald-400 focus-within:border-emerald-400" data-testid={testid}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50 rounded-t-lg">
        <ToolbarButton
          active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          title="Titlu mare (H1)" testid="rte-h1"
        ><Heading1 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Subtitlu (H2)" testid="rte-h2"
        ><Heading2 className="w-4 h-4" /></ToolbarButton>

        <ToolbarSep />

        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold (Ctrl+B)" testid="rte-bold"
        ><Bold className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic (Ctrl+I)" testid="rte-italic"
        ><Italic className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline (Ctrl+U)" testid="rte-underline"
        ><UnderlineIcon className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          title="Strikethrough" testid="rte-strike"
        ><Strikethrough className="w-4 h-4" /></ToolbarButton>

        <ToolbarSep />

        {/* Text color */}
        <div className="relative" ref={colorBtnRef}>
          <ToolbarButton
            onClick={() => { setShowColors((s) => !s); setShowEmoji(false); }}
            title="Culoare text" testid="rte-color-btn"
          ><Palette className="w-4 h-4" /></ToolbarButton>
          {showColors && (
            <div className="absolute z-30 top-9 left-0 bg-white border border-slate-200 rounded-lg shadow-lg p-2 grid grid-cols-4 gap-1 w-40">
              {TEXT_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => {
                    editor.chain().focus().setColor(c.value).run();
                    setShowColors(false);
                  }}
                  title={c.name}
                  className="w-8 h-8 rounded border border-slate-200 hover:scale-110 transition"
                  style={{ backgroundColor: c.value }}
                  data-testid={`rte-color-${c.name.toLowerCase()}`}
                />
              ))}
              <button
                type="button"
                onClick={() => { editor.chain().focus().unsetColor().run(); setShowColors(false); }}
                className="col-span-4 mt-1 text-[10px] text-slate-500 hover:text-slate-900 uppercase tracking-widest font-bold"
                data-testid="rte-color-reset"
              >Reset</button>
            </div>
          )}
        </div>

        <ToolbarButton
          active={editor.isActive("highlight")}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          title="Evidențiere galbenă" testid="rte-highlight"
        ><Highlighter className="w-4 h-4" /></ToolbarButton>

        <ToolbarSep />

        <ToolbarButton
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Listă cu bulete" testid="rte-ul"
        ><List className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Listă numerotată" testid="rte-ol"
        ><ListOrdered className="w-4 h-4" /></ToolbarButton>

        <ToolbarSep />

        <ToolbarButton
          active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          title="Aliniat stânga" testid="rte-align-left"
        ><AlignLeft className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          title="Centrat" testid="rte-align-center"
        ><AlignCenter className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          title="Aliniat dreapta" testid="rte-align-right"
        ><AlignRight className="w-4 h-4" /></ToolbarButton>

        <ToolbarSep />

        <ToolbarButton
          active={editor.isActive("link")}
          onClick={addLink}
          title="Adaugă/modifică link" testid="rte-link"
        ><Link2 className="w-4 h-4" /></ToolbarButton>
        {editor.isActive("link") && (
          <ToolbarButton
            onClick={() => editor.chain().focus().unsetLink().run()}
            title="Elimină link" testid="rte-unlink"
          ><Link2Off className="w-4 h-4" /></ToolbarButton>
        )}

        {onAddPhoto && (
          <>
            <ToolbarSep />
            <ToolbarButton
              onClick={async () => {
                // Capture the project context AT the moment the button is
                // clicked — using the cursor position, not "last project in
                // the whole document". Fixes the bug where photos landed on
                // the very last project pill regardless of where the caret
                // actually was. See planEditorHelpers.getNearestProjectId.
                const contextProjectId = getNearestProjectId(editor);
                const res = await onAddPhoto(contextProjectId);
                if (!res) return;
                // Support both single object and array of {url,label} from picker.
                const photos = Array.isArray(res) ? res : [res];
                if (photos.length === 0) return;
                const chunks = photos.map(({ url, label }) => {
                  const safeLabel = (label || "Poza").replace(/</g, "&lt;").replace(/>/g, "&gt;");
                  const safeUrl = (url || "").replace(/"/g, "%22");
                  return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">📷 ${safeLabel}</a>`;
                });
                editor
                  .chain()
                  .focus()
                  .insertContent(chunks.join(" ") + "&nbsp;")
                  .run();
              }}
              title="Adaugă poze"
              testid="rte-photo"
            ><Camera className="w-4 h-4" /></ToolbarButton>
          </>
        )}

        {onAddMaterialList && (
          <ToolbarButton
            onClick={async () => {
              // Same context-capture logic as the photo button — the material
              // list must attach to the project pill nearest the CURSOR, not
              // the last project in the whole plan body.
              const contextProjectId = getNearestProjectId(editor);
              const res = await onAddMaterialList(contextProjectId);
              if (!res) return;
              const { id, title } = res;
              const safeTitle = (title || "Listă materiale").replace(/</g, "&lt;").replace(/>/g, "&gt;");
              editor
                .chain()
                .focus()
                .insertContent(`<a href="#list:${id}" data-mlist="${id}">📋 ${safeTitle}</a>&nbsp;`)
                .run();
            }}
            title="Adaugă listă materiale"
            testid="rte-material-list"
          ><ClipboardList className="w-4 h-4" /></ToolbarButton>
        )}

        {onAddProject && (
          <ToolbarButton
            onClick={async () => {
              const res = await onAddProject();
              if (!res) return;
              const { id, number, name, address } = res;
              // Address is used as the primary project label in Doserne.
              // If missing, fall back to project name.
              const primary = address || name || "";
              const label = [number ? `#${number}` : "", primary].filter(Boolean).join(" · ");
              const safeLabel = label.replace(/</g, "&lt;").replace(/>/g, "&gt;");
              // Insert as a block-ish anchor so it stands out visually.
              editor
                .chain()
                .focus()
                .insertContent(`<a href="#project:${id}" data-project="${id}">🏗️ ${safeLabel}</a>&nbsp;`)
                .run();
            }}
            title="Adaugă proiect"
            testid="rte-project"
          ><HardHat className="w-4 h-4" /></ToolbarButton>
        )}

        <ToolbarSep />

        {/* Emoji picker */}
        <div className="relative" ref={emojiBtnRef}>
          <ToolbarButton
            active={showEmoji}
            onClick={() => { setShowEmoji((s) => !s); setShowColors(false); }}
            title="Emoji" testid="rte-emoji-btn"
          ><Smile className="w-4 h-4" /></ToolbarButton>
          {showEmoji && (
            <div className="absolute z-40 top-9 right-0" data-testid="rte-emoji-panel">
              <EmojiPicker
                onEmojiClick={(e) => {
                  editor.chain().focus().insertContent(e.emoji).run();
                  setShowEmoji(false);
                }}
                theme={Theme.LIGHT}
                emojiStyle={EmojiStyle.NATIVE}
                searchPlaceHolder="Caută emoji..."
                width={320}
                height={380}
                lazyLoadEmojis
              />
            </div>
          )}
        </div>
      </div>

      {/* Editor surface */}
      <EditorContent
        editor={editor}
        className="max-w-none px-3 py-2 text-sm text-slate-900 focus:outline-none [&_.ProseMirror]:min-h-[160px] [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child]:before:text-slate-400 [&_.ProseMirror_p.is-editor-empty:first-child]:before:float-left [&_.ProseMirror_p.is-editor-empty:first-child]:before:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child]:before:h-0 [&_.ProseMirror_h1]:text-2xl [&_.ProseMirror_h1]:font-black [&_.ProseMirror_h1]:mt-2 [&_.ProseMirror_h1]:mb-1 [&_.ProseMirror_h1]:text-slate-900 [&_.ProseMirror_h2]:text-lg [&_.ProseMirror_h2]:font-bold [&_.ProseMirror_h2]:mt-2 [&_.ProseMirror_h2]:mb-1 [&_.ProseMirror_h2]:text-slate-900 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_a]:underline [&_.ProseMirror_a]:text-emerald-700 [&_.ProseMirror_p]:my-1 [&_.ProseMirror_strong]:font-bold [&_.ProseMirror_em]:italic [&_.ProseMirror_u]:underline [&_.ProseMirror_s]:line-through [&_.ProseMirror_mark]:bg-yellow-200 [&_.ProseMirror_mark]:rounded [&_.ProseMirror_mark]:px-0.5"
        style={{ minHeight }}
      />
    </div>
  );
}
